import { ProductSelectedOptionItem } from '@models/commerce';
import { ProductOption } from '@models/product';
import moment from 'moment';
import { getDateFromString } from '@utils/product-option-date-parse';
import { getUniqueKeyFromUUID } from './string';

const TIME_STEP_PATTERN = /(오전|오후|\d{1,2}\s*:\s*\d{2}|\d{1,2}\s*시)/;

export const normalizeYyyymmdd = (value?: string | null): string | null => {
  if (!value) return null;
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return null;
};

export const optionHasDate = (option: ProductOption): boolean => {
  if (option.date || option.yyyymmdd) {
    return true;
  }
  const stepText = (option.step || []).join(' / ');
  return Boolean(getDateFromString(stepText));
};

export const getOptionDateKey = (option: ProductOption): string | null => {
  const fromYyyymmdd = normalizeYyyymmdd(option.yyyymmdd);
  if (fromYyyymmdd) {
    return fromYyyymmdd;
  }
  if (option.date) {
    const parsed = moment(option.date);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }
  }
  const stepText = (option.step || []).join(' / ');
  const fromStep = getDateFromString(stepText);
  return fromStep ? moment(fromStep).format('YYYY-MM-DD') : null;
};

export const isCalendarOptionProduct = (options?: ProductOption[], flags?: { forceOptionModal?: boolean }): boolean => {
  if (flags?.forceOptionModal) {
    return false;
  }
  if (!options || options.length === 0) {
    return false;
  }
  return options.every((option) => option.type === 'sequential' && optionHasDate(option));
};

export const groupOptionsByDateKey = (options: ProductOption[]): Map<string, ProductOption[]> => {
  const map = new Map<string, ProductOption[]>();
  options.forEach((option) => {
    const key = getOptionDateKey(option);
    if (!key) return;
    const list = map.get(key) || [];
    list.push(option);
    map.set(key, list);
  });
  return map;
};

export const getTodayDateKey = (): string => moment().format('YYYY-MM-DD');

export const isDatePast = (dateKey: string, todayKey: string = getTodayDateKey()): boolean => {
  return dateKey < todayKey;
};

export const isDateSoldOut = (optionsForDay: ProductOption[]): boolean => {
  if (!optionsForDay.length) return true;
  return optionsForDay.every((option) => (option.inventory ?? 0) <= 0);
};

/** CommerceOptionModal 마감임박과 동일: inventory > 0 && inventory < 10 */
export const isClosingSoonInventory = (inventory?: number): boolean => {
  const remain = inventory ?? 0;
  return remain > 0 && remain < 10;
};

/** 해당 날짜 옵션 중 마감임박(잔여 1~9)이 하나라도 있으면 true */
export const isDateClosingSoon = (optionsForDay: ProductOption[]): boolean => {
  if (!optionsForDay.length) return false;
  return optionsForDay.some((option) => isClosingSoonInventory(option.inventory));
};

/** 캘린더에서 선택 불가한 날짜 (지난 날짜만). 매진 날짜는 클릭 가능하며 이후 step에서 비활성 처리 */
export const isDateDisabled = (
  dateKey: string,
  _optionsForDay: ProductOption[],
  todayKey: string = getTodayDateKey(),
): boolean => {
  return isDatePast(dateKey, todayKey);
};

export const getDateStepIndex = (option: ProductOption): number => {
  const key = getOptionDateKey(option);
  if (!key || !option.step?.length) return 0;
  for (let i = 0; i < option.step.length; i++) {
    const parsed = getDateFromString(option.step[i]);
    if (parsed && moment(parsed).format('YYYY-MM-DD') === key) {
      return i;
    }
  }
  return 0;
};

export const isTimeStepValue = (value?: string): boolean => {
  if (!value) return false;
  return TIME_STEP_PATTERN.test(value);
};

export const splitAmPmTimeValues = (values: string[]): { am: string[]; pm: string[]; other: string[] } => {
  const am: string[] = [];
  const pm: string[] = [];
  const other: string[] = [];
  values.forEach((value) => {
    if (/오전/.test(value) || /\b(AM|am)\b/.test(value)) {
      am.push(value);
    } else if (/오후/.test(value) || /\b(PM|pm)\b/.test(value)) {
      pm.push(value);
    } else {
      const hourMatch = value.match(/(\d{1,2})\s*:\s*\d{2}/) || value.match(/(\d{1,2})\s*시/);
      if (hourMatch) {
        const hour = Number(hourMatch[1]);
        if (hour < 12) am.push(value);
        else pm.push(value);
      } else {
        other.push(value);
      }
    }
  });
  return { am, pm, other };
};

export interface CalendarCartAdditionalOption {
  name: string;
  count: number;
  fee: number;
}

export interface CalendarCartLine {
  id: string;
  optionId: string;
  optionName: string;
  optionCount: number;
  optionFee: number;
  additionalOptions: CalendarCartAdditionalOption[];
  lineTotal: number;
}

export const getProductSaleFee = (product: {
  fee?: number;
  originFee?: number;
  applyPeriodDiscount?: boolean;
  discountInfo?: {
    discountedFee?: number;
    discountStartDate?: Date | string;
    discountEndDate?: Date | string;
  };
  periodDiscountExcludeChannels?: string[];
}): number => {
  const excludeDiscount = (product.periodDiscountExcludeChannels || []).includes('app');
  if (
    product.applyPeriodDiscount &&
    product.discountInfo?.discountedFee != null &&
    !excludeDiscount &&
    product.discountInfo.discountStartDate &&
    product.discountInfo.discountEndDate
  ) {
    const today = new Date();
    if (
      new Date(product.discountInfo.discountStartDate) < today &&
      new Date(product.discountInfo.discountEndDate) > today
    ) {
      return product.discountInfo.discountedFee || 0;
    }
  }
  return product.fee || 0;
};

/** sequential 옵션 단가 = 상품 판매가 + 옵션가 (CommerceOptionModal과 동일) */
export const getCalendarOptionUnitFee = (option: ProductOption, productFee: number): number => {
  return (option.fee || 0) + (productFee || 0);
};

export const buildCalendarCartLine = (params: {
  option: ProductOption;
  optionCount: number;
  additionalOptions: CalendarCartAdditionalOption[];
  productFee: number;
}): CalendarCartLine => {
  const optionName = (params.option.step || []).join(' / ');
  const additionalTotal = params.additionalOptions.reduce((sum, item) => sum + item.fee * item.count, 0);
  const optionFee = getCalendarOptionUnitFee(params.option, params.productFee);
  return {
    id: getUniqueKeyFromUUID(),
    optionId: params.option._id || '',
    optionName,
    optionCount: params.optionCount,
    optionFee,
    additionalOptions: params.additionalOptions,
    lineTotal: optionFee * params.optionCount + additionalTotal,
  };
};

export const convertCalendarCartToCheckoutPayload = (
  lines: CalendarCartLine[],
): {
  selectedOptions: ProductSelectedOptionItem[];
  totalFee: number;
} => {
  const selectedOptions: ProductSelectedOptionItem[] = [];

  lines.forEach((line) => {
    selectedOptions.push({
      optionName: line.optionName,
      optionCount: line.optionCount,
      optionFee: line.optionFee,
    });

    line.additionalOptions.forEach((item) => {
      if (item.count <= 0) return;
      selectedOptions.push({
        optionName: item.name,
        optionCount: item.count,
        optionFee: item.fee,
      });
    });
  });

  return {
    selectedOptions,
    totalFee: lines.reduce((sum, line) => sum + line.lineTotal, 0),
  };
};
